import type { ReportFilterKey, ReportOptionFilterKey, ReportQuery } from '@chukua/common';
import { Button, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { hasAsOf, hasPeriod, pickerFilters } from '../policies/visible-filters.policy';
import { ReportFilterSelect } from './ReportFilterSelect';

const DATE_FORMAT = 'YYYY-MM-DD';

/** The slice of a report query the bar reads and writes; both tabs' queries have it. */
export type ReportFilterValues = Partial<Pick<ReportQuery, ReportFilterKey>>;

interface ReportFilterBarProps {
  declared: ReportFilterKey[];
  values: ReportFilterValues;
  onChange: (patch: ReportFilterValues) => void;
  onClear: () => void;
  isFiltered: boolean;
}

function PeriodPicker({ values, onChange }: Pick<ReportFilterBarProps, 'values' | 'onChange'>) {
  return (
    <DatePicker.RangePicker
      aria-label="Period"
      className="w-full sm:w-auto"
      value={[values.from ? dayjs(values.from) : null, values.to ? dayjs(values.to) : null]}
      allowEmpty={[true, true]}
      onChange={(range) => onChange({ from: range?.[0]?.format(DATE_FORMAT), to: range?.[1]?.format(DATE_FORMAT) })}
    />
  );
}

function AsOfPicker({ values, onChange }: Pick<ReportFilterBarProps, 'values' | 'onChange'>) {
  return (
    <DatePicker
      aria-label="As of"
      placeholder="As of"
      className="w-full sm:w-auto"
      value={values.asOf ? dayjs(values.asOf) : null}
      onChange={(date) => onChange({ asOf: date?.format(DATE_FORMAT) })}
    />
  );
}

/** Only the filters the report declares appear; the rest are not disabled, they are absent. State lives in the URL, so a link reproduces the view. */
export function ReportFilterBar({ declared, values, onChange, onClear, isFiltered }: ReportFilterBarProps) {
  return (
    <div role="search" aria-label="Report filters" className="mb-4 flex flex-wrap items-center gap-2">
      {hasPeriod(declared) && <PeriodPicker values={values} onChange={onChange} />}
      {hasAsOf(declared) && <AsOfPicker values={values} onChange={onChange} />}
      {pickerFilters(declared).map((filter: ReportOptionFilterKey) => (
        <ReportFilterSelect
          key={filter}
          filter={filter}
          values={values[filter] ?? []}
          onChange={(selected) => onChange({ [filter]: selected } as ReportFilterValues)}
        />
      ))}
      {isFiltered && (
        <Button type="link" onClick={onClear}>
          Clear filters
        </Button>
      )}
    </div>
  );
}
